import { useState, useEffect, useRef } from "react";
import { Trash2, Check, X, Plus, Pencil, Upload, Star, Image as ImageIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const emptyForm = { name: "", role: "", content: "", rating: 5, image: "" };

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { getToken } = useAuth();
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get(`${API}/admin/testimonials`, { headers });
      setTestimonials(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTestimonials(); }, []);

  const openAdd = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setForm({ name: t.name, role: t.role || "", content: t.content, rating: t.rating || 5, image: t.image || "" });
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await axios.post(`${API}/admin/upload-image`, fd, { headers });
      setForm(prev => ({ ...prev, image: res.data.url }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.content) {
      toast.error("Name and content are required");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/admin/testimonials/${editId}`, form, { headers });
        toast.success("Testimonial updated");
      } else {
        await axios.post(`${API}/admin/testimonials`, form, { headers });
        toast.success("Testimonial created");
      }
      setDialogOpen(false);
      fetchTestimonials();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const toggleApproval = async (id, approved) => {
    try {
      await axios.put(`${API}/admin/testimonials/${id}`, { approved: !approved }, { headers });
      toast.success(approved ? "Testimonial hidden" : "Testimonial approved");
      fetchTestimonials();
    } catch { toast.error("Update failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { headers });
      toast.success("Deleted");
      fetchTestimonials();
    } catch { toast.error("Delete failed"); }
  };

  const imgSrc = (url) => url?.startsWith("/api") ? `${API.replace('/api', '')}${url}` : url;

  return (
    <div className="space-y-4" data-testid="admin-testimonials">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-gray-900">Testimonials ({testimonials.length})</h1>
        <Button onClick={openAdd} className="bg-praroha-forest hover:bg-praroha-forest/90 text-white rounded-full" data-testid="add-testimonial-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Testimonial
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-gray-200 animate-pulse rounded-lg" />)}</div>
      ) : testimonials.length === 0 ? (
        <Card className="border-gray-200"><CardContent className="p-8 text-center text-gray-400">No testimonials yet.</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {testimonials.map((t) => (
            <Card key={t.id} className={`border-gray-200 ${!t.approved ? "opacity-60 border-dashed" : ""}`} data-testid={`testimonial-${t.id?.slice(0,8)}`}>
              <CardContent className="p-4 flex items-start gap-4">
                {/* Avatar */}
                <div className="w-11 h-11 rounded-full bg-praroha-forest/10 flex items-center justify-center overflow-hidden shrink-0">
                  {t.image ? (
                    <img src={imgSrc(t.image)} alt={t.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-heading font-bold text-praroha-forest text-sm">{t.name?.[0]?.toUpperCase()}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-semibold text-gray-800">{t.name}</span>
                    <span className="text-xs text-gray-400">{t.role}</span>
                    <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-medium ${t.approved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                      {t.approved ? "Approved" : "Pending"}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">{t.content}</p>
                  <div className="flex items-center gap-1 mt-1">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className={`text-xs ${i < t.rating ? "text-amber-400" : "text-gray-200"}`}>&#9733;</span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(t)} className="text-gray-500 hover:text-praroha-forest" data-testid={`edit-testimonial-${t.id?.slice(0,8)}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleApproval(t.id, t.approved)} className={t.approved ? "text-amber-500" : "text-green-600"}>
                    {t.approved ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(t.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="testimonial-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editId ? "Edit Testimonial" : "Add Testimonial"}</DialogTitle>
            <DialogDescription>Fill in the details below. Profile picture is optional.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Profile Picture */}
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-full bg-praroha-forest/10 flex items-center justify-center overflow-hidden cursor-pointer border-2 border-dashed border-gray-300 hover:border-praroha-sage transition-colors group relative"
                onClick={() => fileRef.current?.click()}
                data-testid="testimonial-image-upload"
              >
                {form.image ? (
                  <img src={imgSrc(form.image)} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-gray-300 group-hover:text-praroha-sage" />
                )}
                {uploading && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>}
              </div>
              <div>
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="rounded-full text-xs" data-testid="upload-profile-pic-btn">
                  <Upload className="w-3 h-3 mr-1.5" /> {form.image ? "Change Photo" : "Upload Photo"}
                </Button>
                {form.image && (
                  <button type="button" onClick={() => setForm(prev => ({ ...prev, image: "" }))} className="text-xs text-red-500 ml-2 hover:underline">Remove</button>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} data-testid="testimonial-image-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="t-name">Name *</Label>
                <Input id="t-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Customer name" data-testid="testimonial-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="t-role">Role</Label>
                <Input id="t-role" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} placeholder="e.g., Home Chef" data-testid="testimonial-role-input" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Rating</Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button key={star} type="button" onClick={() => setForm(p => ({ ...p, rating: star }))} className="p-0.5" data-testid={`testimonial-star-${star}`}>
                    <Star className={`w-6 h-6 ${star <= form.rating ? "fill-amber-400 text-amber-400" : "text-gray-200"}`} />
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="t-content">Review *</Label>
              <Textarea id="t-content" value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} placeholder="Customer review content..." rows={3} data-testid="testimonial-content-input" />
            </div>

            {/* Image URL paste */}
            <div className="space-y-1.5">
              <Label htmlFor="t-imgurl" className="text-xs text-muted-foreground">Or paste image URL</Label>
              <Input id="t-imgurl" value={form.image} onChange={e => setForm(p => ({ ...p, image: e.target.value }))} placeholder="https://..." className="text-xs" data-testid="testimonial-image-url-input" />
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full bg-praroha-forest hover:bg-praroha-forest/90 text-white rounded-full" data-testid="save-testimonial-btn">
              {saving ? "Saving..." : editId ? "Update Testimonial" : "Create Testimonial"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
